export function formatCurrency(amount: number, showSign: boolean = false): string {
  const formatted = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Math.abs(amount))

  if (amount < 0) {
    return `(${formatted.replace('$', '$')})`
  }
  
  if (showSign && amount > 0) {
    return `+${formatted}`
  }

  return formatted
}

export function formatPercent(value: number, decimals: number = 1): string {
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(decimals)}%`
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date)
}

export function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

export function getRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return formatDate(dateString)
}

export function getPeriodLabel(startDate: string, endDate: string): string {
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    return new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric' }).format(start)
  }
  
  if (start.getFullYear() === end.getFullYear()) {
    return `${new Intl.DateTimeFormat('en-AU', { month: 'short' }).format(start)} - ${new Intl.DateTimeFormat('en-AU', { month: 'short', year: 'numeric' }).format(end)}`
  }
  
  return `${formatDate(startDate)} - ${formatDate(endDate)}`
}

export function getFinancialYearDates(year?: number): { startDate: string; endDate: string } {
  const now = new Date()
  const currentYear = year || now.getFullYear()
  
  const fyStart = new Date(currentYear, 6, 1)
  const fyEnd = new Date(currentYear + 1, 5, 30)
  
  return {
    startDate: fyStart.toISOString().split('T')[0],
    endDate: fyEnd.toISOString().split('T')[0]
  }
}

export function getQuarterDates(quarter: 1 | 2 | 3 | 4, year?: number): { startDate: string; endDate: string } {
  const currentYear = year || new Date().getFullYear()
  
  const quarters = {
    1: { start: new Date(currentYear, 6, 1), end: new Date(currentYear, 8, 30) },
    2: { start: new Date(currentYear, 9, 1), end: new Date(currentYear, 11, 31) },
    3: { start: new Date(currentYear + 1, 0, 1), end: new Date(currentYear + 1, 2, 31) },
    4: { start: new Date(currentYear + 1, 3, 1), end: new Date(currentYear + 1, 5, 30) }
  }
  
  return {
    startDate: quarters[quarter].start.toISOString().split('T')[0],
    endDate: quarters[quarter].end.toISOString().split('T')[0]
  }
}

export function getMonthDates(month: number, year: number): { startDate: string; endDate: string } {
  const start = new Date(year, month, 1)
  const end = new Date(year, month + 1, 0)
  
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0]
  }
}

/** The product is Australian: every user-visible date and time uses this locale. */
export const DISPLAY_LOCALE = 'en-AU'

/** e.g. '5 Nov 2026'. */
export const DISPLAY_DATE_FORMAT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }

/** e.g. '5 Nov 2026, 9:05 am'. */
export const DISPLAY_DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  ...DISPLAY_DATE_FORMAT,
  hour: 'numeric',
  minute: '2-digit',
}

/** Local calendar day of an instant, e.g. '5 Nov 2026'. Use for timestamps (activity, messages, uploads). */
export function formatLocalDate(date: Date): string {
  return date.toLocaleDateString(DISPLAY_LOCALE, DISPLAY_DATE_FORMAT)
}

/** Local day and time of an instant, e.g. '5 Nov 2026, 9:05 am'. */
export function formatLocalDateTime(date: Date): string {
  return date.toLocaleString(DISPLAY_LOCALE, DISPLAY_DATE_TIME_FORMAT)
}

/**
 * A date-only value (a due date) as a display date, e.g. '5 Nov 2026'. Null when missing or unparseable.
 * Date-only values are stored as UTC midnight, so they are formatted in UTC: local parts would show the
 * previous day west of UTC.
 */
export function formatDateOnly(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString(DISPLAY_LOCALE, { ...DISPLAY_DATE_FORMAT, timeZone: 'UTC' })
}
